import 'focus-visible';
import '@styles/theme.scss';
import '@styles/base.scss';
import '@styles/chrome-bug.css';
import '@styles/algolia.scss';

import { FC, ReactNode, useEffect, useRef } from 'react';
import { NextIntlClientProvider } from 'next-intl';
import { getMessageFallback } from '../i18n/getMessageFallback';
import { useTranslation, FlatMessagesContext } from 'next-i18next';
import TagManager from 'react-gtm-module';
import type { AppProps } from 'next/app';
import Head from '@components/global/Head';
import { GlobalUIContext } from '@context/GlobalUIContext';
import { CartContextProvider } from '@context/CartContext';
import { SaveForLaterContextProvider } from '@context/SaveForLaterContext';
import { ComparisonContextProvider } from '@context/CompareContext';
import { FeatureFlagsProvider } from '@context/FeatureFlagsContext';
import { AlgoliaSearchKeyProvider } from '@context/AlgoliaSearchKeyContext';
import type { AlgoliaSearchKey } from '@context/AlgoliaSearchKeyContext';
import IceChatButton from '@components/global/IceChatButton';
import { StoresUIContextProvider } from '@context/StoresContext';
import { ReturnsContextProvider } from '@context/ReturnsContext';
import { useRouter } from 'next/router';
import { CustomerProvider } from '@context/CustomerContext';
import { ShadowDomGTM } from '@components/global/ShadowDomGTM';
import { Store } from '@adapters/prismic/fetching/stores/types';
import * as Noibu from 'noibu-react';
import checkSDKExistanceAndAddCustomAttribute from '@utils/noibu/checkSDKExistanceAndAddCustomAttribute';
import Cookies from 'js-cookie';
import { useCustomer } from '@adapters/bigcommerce/hooks/customer';
import analytics from '@utils/gtm/ga4/checkout';
import { Inter, Roboto_Condensed } from 'next/font/google';
import { initAlgoliaAnalytics } from '@utils/algolia/analytics';
import { PrismicPreview } from '@prismicio/next/pages';
import { repositoryName } from '@adapters/prismic/client';
import { Tooltip } from '@base-ui/react/tooltip';

const inter = Inter({
  weight: ['400', '500', '600'],
  subsets: ['latin'],
  display: 'swap',
  fallback: ['Arial'],
  variable: '--font-inter',
});

const robotoCondensed = Roboto_Condensed({
  weight: ['400', '500', '700'],
  style: ['normal', 'italic'],
  subsets: ['latin'],
  variable: '--font-roboto-condensed',
  display: 'swap',
  fallback: ['sans-serif'],
});

const tagManagerArgs = {
  gtmId: process.env.NEXT_PUBLIC_GTM_CONTAINER_ID ?? '',
};

let oneTrustActiveGroups;
if (typeof window !== 'undefined') {
  oneTrustActiveGroups = window.OnetrustActiveGroups;
}

const oneTrustPerformanceIsActive = oneTrustActiveGroups?.includes('C0002');

async function importDatadog() {
  const { datadogRum } = await import('@datadog/browser-rum');
  if (datadogRum) {
    datadogRum.init({
      applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID ?? '',
      clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ?? '',
      site: process.env.NEXT_PUBLIC_DATADOG_SITE,
      service: process.env.NEXT_PUBLIC_DATADOG_SERVICE,
      env: process.env.NEXT_PUBLIC_DATADOG_ENV,
      version: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
      sampleRate: parseInt(process.env.NEXT_PUBLIC_DATADOG_SAMPLERATE ?? '25'),
      replaySampleRate: 0,
      trackInteractions: true,
      defaultPrivacyLevel: 'mask-user-input',
    });

    datadogRum.startSessionReplayRecording();
  }
}

if (oneTrustPerformanceIsActive) {
  importDatadog();
}

const dataDogRumCookie = Cookies.get('_dd_s');
if (!oneTrustPerformanceIsActive && dataDogRumCookie) {
  Cookies.remove('_dd_s');
}

const isStorePc = Cookies.get('storepc');

interface NoopProps {
  children: ReactNode;
}

const Noop: FC<NoopProps> = ({ children }: NoopProps) => <>{children}</>;

const AnalyticsWrapper = ({ children }: NoopProps) => {
  const { route, asPath, query } = useRouter();
  const locale = (query.locale as string) || 'en';
  const { customer } = useCustomer();

  const cachedPath = useRef<string | null>(null);
  useEffect(() => {
    if (cachedPath.current === asPath) return;
    cachedPath.current = asPath;

    analytics.pageView({ path: asPath, locale, customer, route });
  }, [asPath, customer, locale, route]);

  return <>{children}</>;
};

const App = ({
  Component,
  pageProps,
}: AppProps<{
  stores: Store[];
  featureFlags: Record<string, unknown>;
  algoliaSearchKey?: AlgoliaSearchKey | null;
}>) => {
  const Layout = (Component as any).Layout || Noop;
  const { asPath } = useRouter();
  const hasStorePcQueryParam = asPath.includes('storepc=');
  const { t } = useTranslation('common');

  if (hasStorePcQueryParam) {
    Cookies.set('storepc', 'true', { expires: 365 });
  }

  useEffect(() => {
    // Store PCs use Edge browser in privacy mode, which circumvents the internal traffic
    // filtering we have setup in GA (that uses IPs) so instead we just don't load GTM at all
    if (!hasStorePcQueryParam && !isStorePc) {
      TagManager.initialize(tagManagerArgs);
    }

    document.body.classList?.remove('loading');
    checkSDKExistanceAndAddCustomAttribute();
  }, [hasStorePcQueryParam, t]);

  useEffect(() => {
    // algolia analytics uses cookies, so let's wait for OneTrust
    initAlgoliaAnalytics();
    window.addEventListener('OneTrustGroupsUpdated', initAlgoliaAnalytics);

    return () => {
      window.removeEventListener('OneTrustGroupsUpdated', initAlgoliaAnalytics);
    };
  }, []);

  return (
    <>
      <style jsx global>{`
        :root {
          --font-inter: ${inter.style.fontFamily};
          --font-roboto-condensed: ${robotoCondensed.style.fontFamily};
        }
      `}</style>
      <Head />
      <FeatureFlagsProvider flags={pageProps.featureFlags || {}}>
        <AlgoliaSearchKeyProvider initialKey={pageProps.algoliaSearchKey}>
          <Tooltip.Provider>
            <GlobalUIContext>
              <CustomerProvider>
                <CartContextProvider>
                  <StoresUIContextProvider stores={pageProps.stores}>
                    <SaveForLaterContextProvider>
                      <ComparisonContextProvider>
                        <ReturnsContextProvider>
                          <Layout pageProps={pageProps}>
                            <AnalyticsWrapper>
                              <Noibu.ErrorBoundary
                                onError={(error, componentStack, eventId) => {
                                  console.error(error, componentStack, eventId);
                                }}
                              >
                                <Component {...pageProps} />
                                <PrismicPreview
                                  repositoryName={repositoryName}
                                />
                              </Noibu.ErrorBoundary>
                            </AnalyticsWrapper>
                          </Layout>
                        </ReturnsContextProvider>
                      </ComparisonContextProvider>
                    </SaveForLaterContextProvider>
                  </StoresUIContextProvider>
                </CartContextProvider>
              </CustomerProvider>
            </GlobalUIContext>
          </Tooltip.Provider>
        </AlgoliaSearchKeyProvider>
      </FeatureFlagsProvider>
      <IceChatButton />
      <ShadowDomGTM />
    </>
  );
};

function AppWithProviders(
  props: AppProps<{
    stores: Store[];
    featureFlags: Record<string, unknown>;
    messages?: Record<string, unknown>;
    flatMessages?: Record<string, Record<string, string>>;
  }>
) {
  const router = useRouter();
  const rawMessages = props.pageProps.messages || {};
  const safeMessages: Record<string, unknown> =
    typeof rawMessages === 'object' && rawMessages !== null
      ? {
          ...rawMessages,
          common: (rawMessages as Record<string, unknown>).common ?? {},
        }
      : { common: {} };

  return (
    <NextIntlClientProvider
      locale={(router.query.locale as string) || 'en'}
      messages={safeMessages}
      getMessageFallback={getMessageFallback}
    >
      <FlatMessagesContext.Provider value={props.pageProps.flatMessages || {}}>
        <App {...props} />
      </FlatMessagesContext.Provider>
    </NextIntlClientProvider>
  );
}

export default AppWithProviders;
